import { resolveUserRedirect } from "@/utils/routerBeforeEach";

// Keeps the current user fresh at runtime and reacts to what the refresh
// reveals. The fetch-and-store logic lives in `userStore.refresh()`; this
// composable only owns the *triggers* (route change, tab focus, bfcache/back)
// and the *navigation* response (session expiry → login, role change → guard
// redirect). Initial load is refreshed once at bootstrap (see `main.ts`).
export const useRefetchUsers = (
  opts: { force: boolean } = { force: false }
): void => {
  const route = useRoute();
  const router = useRouter();

  const userStore = useUserStore();

  const shouldFetchUser = computed(() => {
    return (
      userStore.isAuthenticated &&
      (["home", "gallery-index", "folders-index"].includes(
        route.name as string
      ) ||
        opts.force)
    );
  });

  // `bypassRouteGate` forces a refresh on any authenticated route. The
  // reactivation triggers (tab focus, bfcache/back restore) use it because the
  // displayed user can be stale regardless of which route we're on.
  const refresh = async (bypassRouteGate = false): Promise<void> => {
    if (bypassRouteGate ? !userStore.isAuthenticated : !shouldFetchUser.value) {
      return;
    }

    // Snapshot the role before refreshing, because refresh() clears the store
    // on expiry and we need to know how to route the user afterwards.
    const isPupilBeforeRefresh = userStore.isPupil;
    const result = await userStore.refresh();

    if (result === "expired") {
      // refresh() has already cleared the session; route the user to re-auth.
      // Pupils re-enter through the /go landing page (full reload, like
      // logout); teachers go to the login route with a return path.
      if (isPupilBeforeRefresh) {
        window.location.replace("/go");
      } else if (route.name !== "login") {
        void router.push({
          name: "login",
          query: { redirectUri: route.fullPath },
        });
      }
      return;
    }

    // A refresh can arrive without a navigation (tab focus, bfcache/back), so
    // the router guard never runs. Re-apply the role/auth redirects here so
    // fresh data still moves the user off a page they no longer belong on.
    const redirect = resolveUserRedirect(route, userStore);
    if (redirect) {
      void router.push(redirect);
    }
  };

  // Watch `fullPath`, not `route.name`: reused route records (a detail page
  // whose `id`/query changes without a name change) must still refresh.
  watch(
    () => route.fullPath,
    () => {
      void refresh();
    }
  );

  // Refresh when the tab becomes visible again after being inactive — the user
  // may have changed plan/state in another tab or on another device.
  useEventListener(document, "visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void refresh(true);
    }
  });

  // Refresh on browser back that restores the page from the bfcache. A bfcache
  // restore fires `pageshow` with `persisted: true` and does NOT re-run setup,
  // so the route watcher above never sees it.
  useEventListener(window, "pageshow", (event: PageTransitionEvent) => {
    if (event.persisted) {
      void refresh(true);
    }
  });
};
